/**
 * Mental Map — the document one person draws, and the vocabulary they draw it with.
 *
 * A mental map is how one person sees a problem, a world or a solution: a few
 * shapes that each mean something, joined by lines that each mean something.
 * The legend below IS the vocabulary — the palette offers exactly these shapes,
 * tones and lines, so every map in a room can be read the same way.
 *
 * The shape meanings follow the committed shape vocabulary shown in the
 * Geometry Studio lab page (src/pages/GeometryStudio.tsx).
 *
 * Everything here is plain data and pure functions: no React, no storage.
 */

export type MapShapeKind =
  | 'circle'
  | 'rectangle'
  | 'rounded'
  | 'triangle'
  | 'diamond'
  | 'hexagon'
  | 'ring'
  | 'point'
  | 'layer'
  | 'phase'
  | 'note';

export type MapTone = 'neutral' | 'priority' | 'tension' | 'working';
export type MapSize = 's' | 'm' | 'l' | 'xl';
export type MapLinkKind = 'leads_to' | 'supports' | 'conflicts' | 'part_of';

export interface MapShape {
  id: string;
  kind: MapShapeKind;
  label: string;
  tone: MapTone;
  size: MapSize;
  x: number;
  y: number;
  /** Set once the shape has been resized by hand; otherwise `size` decides. */
  w?: number;
  h?: number;
}

export interface MapLink {
  id: string;
  from: string;
  to: string;
  kind: MapLinkKind;
  label?: string;
}

export interface MapDoc {
  shapes: MapShape[];
  links: MapLink[];
}

/**
 * The room each person has to draw in. Deliberately far bigger than a screen:
 * a map should never run out of space, and the view zooms and pans over it.
 */
export const AREA_WIDTH = 8000;
export const AREA_HEIGHT = 6000;

export const EMPTY_MAP: MapDoc = { shapes: [], links: [] };

/** How much a shape and a line can say. A note is a sentence or two, not a document. */
export const LABEL_MAX = 280;
export const LINK_LABEL_MAX = 60;

export interface ShapeLegendEntry {
  kind: MapShapeKind;
  name: string;
  meaning: string;
}

export const SHAPE_LEGEND: ShapeLegendEntry[] = [
  { kind: 'circle', name: 'Circle', meaning: 'A whole: a system, a group, a complete thing' },
  { kind: 'rectangle', name: 'Rectangle', meaning: 'A fixed thing: a structure, a category, a fact' },
  { kind: 'rounded', name: 'Rounded box', meaning: 'A step: something that gets done' },
  { kind: 'triangle', name: 'Triangle', meaning: 'A direction: where this is heading, what sits on top' },
  { kind: 'diamond', name: 'Diamond', meaning: 'A decision: a choice that has to be made' },
  { kind: 'hexagon', name: 'Hexagon', meaning: 'A capability: something we can or cannot do' },
  { kind: 'ring', name: 'Ring', meaning: 'A boundary: what is inside the scope and what is not' },
  { kind: 'point', name: 'Point', meaning: 'The focus: the one thing to look at' },
  { kind: 'layer', name: 'Layer', meaning: 'A layer: stack them, and each one rests on the one below' },
  { kind: 'phase', name: 'Phase', meaning: 'A stretch of work: one phase leads into the next' },
  { kind: 'note', name: 'Note', meaning: 'Words on their own: a sentence that belongs beside the picture' },
];

export interface ToneLegendEntry {
  tone: MapTone;
  name: string;
  meaning: string;
  /** Stroke colour — a theme token, so it reads on both Paper and Night. */
  color: string;
}

export const TONE_LEGEND: ToneLegendEntry[] = [
  { tone: 'neutral', name: 'Plain', meaning: 'Just part of the picture', color: 'var(--text-sec)' },
  { tone: 'priority', name: 'Mustard', meaning: 'Matters most', color: 'var(--mustard)' },
  { tone: 'tension', name: 'Coral', meaning: 'A problem or a risk', color: 'var(--coral)' },
  { tone: 'working', name: 'Green', meaning: 'Working well', color: 'var(--green)' },
];

export interface LinkLegendEntry {
  kind: MapLinkKind;
  name: string;
  meaning: string;
  color: string;
  dashed: boolean;
  arrow: boolean;
}

export const LINK_LEGEND: LinkLegendEntry[] = [
  { kind: 'leads_to', name: 'Leads to', meaning: 'This causes or comes before that', color: 'var(--text-sec)', dashed: false, arrow: true },
  { kind: 'supports', name: 'Supports', meaning: 'This helps that', color: 'var(--green)', dashed: false, arrow: true },
  { kind: 'conflicts', name: 'Conflicts with', meaning: 'These pull against each other', color: 'var(--coral)', dashed: true, arrow: false },
  { kind: 'part_of', name: 'Part of', meaning: 'This belongs to that', color: 'var(--text-dim)', dashed: false, arrow: false },
];

export const SIZE_PX: Record<MapSize, number> = { s: 72, m: 120, l: 200, xl: 340 };

export const SIZE_LEGEND: { size: MapSize; name: string }[] = [
  { size: 's', name: 'Small' },
  { size: 'm', name: 'Medium' },
  { size: 'l', name: 'Large' },
  { size: 'xl', name: 'Extra large' },
];

/** Wide shapes: a layer is a long band, a phase is an arrow-shaped block. */
const WIDE_BOX: Partial<Record<MapShapeKind, Record<MapSize, { w: number; h: number }>>> = {
  layer: { s: { w: 320, h: 56 }, m: { w: 480, h: 72 }, l: { w: 680, h: 88 }, xl: { w: 920, h: 104 } },
  phase: { s: { w: 170, h: 60 }, m: { w: 240, h: 80 }, l: { w: 330, h: 104 }, xl: { w: 440, h: 128 } },
  note: { s: { w: 160, h: 56 }, m: { w: 240, h: 84 }, l: { w: 340, h: 116 }, xl: { w: 480, h: 160 } },
};

/** How much room a shape takes on the canvas. Most shapes are square. */
export function shapeBox(kind: MapShapeKind, size: MapSize): { w: number; h: number } {
  return WIDE_BOX[kind]?.[size] ?? { w: SIZE_PX[size], h: SIZE_PX[size] };
}

/** Limits on a hand-resized shape. */
export const MIN_SHAPE = 28;
export const MAX_SHAPE = 4000;

/** The room one particular shape takes: its own size if it was resized, else its preset. */
export function boxOf(shape: Pick<MapShape, 'kind' | 'size' | 'w' | 'h'>): { w: number; h: number } {
  const preset = shapeBox(shape.kind, shape.size);
  return { w: shape.w ?? preset.w, h: shape.h ?? preset.h };
}

/** Shapes that must stay as wide as they are tall, or they stop being what they are. */
export function keepsProportion(kind: MapShapeKind): boolean {
  return kind === 'circle' || kind === 'ring' || kind === 'point';
}

export function mapToneColor(tone: MapTone): string {
  return TONE_LEGEND.find((t) => t.tone === tone)?.color ?? TONE_LEGEND[0].color;
}

export function linkStyle(kind: MapLinkKind): LinkLegendEntry {
  return LINK_LEGEND.find((l) => l.kind === kind) ?? LINK_LEGEND[0];
}

export function nextLinkKind(kind: MapLinkKind): MapLinkKind {
  const i = LINK_LEGEND.findIndex((l) => l.kind === kind);
  return LINK_LEGEND[(i + 1) % LINK_LEGEND.length].kind;
}

let idCounter = 0;
export function newMapId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter.toString(36)}`;
}

/** Keep a shape wholly inside its owner's area. */
export function clampToArea(shape: MapShape): MapShape {
  const { w, h } = boxOf(shape);
  return {
    ...shape,
    x: Math.min(Math.max(0, shape.x), AREA_WIDTH - w),
    y: Math.min(Math.max(0, shape.y), AREA_HEIGHT - h),
  };
}

/** Drop links whose ends no longer exist, and links from a shape to itself. */
export function pruneLinks(doc: MapDoc): MapDoc {
  const ids = new Set(doc.shapes.map((s) => s.id));
  return {
    shapes: doc.shapes,
    links: doc.links.filter((l) => l.from !== l.to && ids.has(l.from) && ids.has(l.to)),
  };
}

/**
 * Read a stored value back into a map, discarding anything that is not one.
 * Maps arrive from other people's browsers, so nothing is trusted by shape.
 */
export function parseMapDoc(value: unknown): MapDoc {
  if (!value || typeof value !== 'object') return EMPTY_MAP;
  const raw = value as { shapes?: unknown; links?: unknown };
  const kinds = new Set(SHAPE_LEGEND.map((s) => s.kind));
  const tones = new Set(TONE_LEGEND.map((t) => t.tone));
  const linkKinds = new Set(LINK_LEGEND.map((l) => l.kind));
  const shapes: MapShape[] = [];
  for (const s of Array.isArray(raw.shapes) ? raw.shapes : []) {
    if (!s || typeof s !== 'object') continue;
    const r = s as Record<string, unknown>;
    if (typeof r.id !== 'string' || !kinds.has(r.kind as MapShapeKind)) continue;
    if (typeof r.x !== 'number' || typeof r.y !== 'number') continue;
    shapes.push(
      clampToArea({
        id: r.id,
        kind: r.kind as MapShapeKind,
        label: typeof r.label === 'string' ? r.label.slice(0, LABEL_MAX) : '',
        tone: tones.has(r.tone as MapTone) ? (r.tone as MapTone) : 'neutral',
        size: r.size === 's' || r.size === 'l' || r.size === 'xl' ? r.size : 'm',
        x: r.x,
        y: r.y,
        ...(typeof r.w === 'number' && typeof r.h === 'number' && r.w > 0 && r.h > 0
          ? { w: Math.min(Math.max(r.w, MIN_SHAPE), MAX_SHAPE), h: Math.min(Math.max(r.h, MIN_SHAPE), MAX_SHAPE) }
          : {}),
      }),
    );
  }
  const links: MapLink[] = [];
  for (const l of Array.isArray(raw.links) ? raw.links : []) {
    if (!l || typeof l !== 'object') continue;
    const r = l as Record<string, unknown>;
    if (typeof r.id !== 'string' || typeof r.from !== 'string' || typeof r.to !== 'string') continue;
    links.push({
      id: r.id,
      from: r.from,
      to: r.to,
      kind: linkKinds.has(r.kind as MapLinkKind) ? (r.kind as MapLinkKind) : 'leads_to',
      label: typeof r.label === 'string' && r.label.trim() ? r.label.slice(0, LINK_LABEL_MAX) : undefined,
    });
  }
  return pruneLinks({ shapes, links });
}

// ── Presentation stage ───────────────────────────────────────────────────────

/** At most four maps are on stage at once; the rest wait in the tray. */
export const STAGE_MAX = 4;

export interface StageTile {
  /** CSS grid placement on a 3-column, 2-row stage. */
  column: string;
  row: string;
}

/**
 * Where each map sits for a given number on stage:
 *   1 — the whole screen
 *   2 — split screen
 *   3 — one large, two stacked beside it
 *   4 — a two-by-two grid
 * A focused map takes the whole screen regardless.
 */
export function stageLayout(count: number): StageTile[] {
  switch (Math.min(Math.max(count, 0), STAGE_MAX)) {
    case 0:
      return [];
    case 1:
      return [{ column: '1 / 7', row: '1 / 3' }];
    case 2:
      return [
        { column: '1 / 4', row: '1 / 3' },
        { column: '4 / 7', row: '1 / 3' },
      ];
    case 3:
      return [
        { column: '1 / 5', row: '1 / 3' },
        { column: '5 / 7', row: '1 / 2' },
        { column: '5 / 7', row: '2 / 3' },
      ];
    default:
      return [
        { column: '1 / 4', row: '1 / 2' },
        { column: '4 / 7', row: '1 / 2' },
        { column: '1 / 4', row: '2 / 3' },
        { column: '4 / 7', row: '2 / 3' },
      ];
  }
}

/** Put a map on stage. A full stage replaces the slot it was dropped on (or the last). */
export function addToStage(stage: string[], id: string, onSlot?: number): string[] {
  if (stage.includes(id)) return stage;
  if (onSlot !== undefined && onSlot >= 0 && onSlot < stage.length) {
    const next = [...stage];
    next[onSlot] = id;
    return next;
  }
  if (stage.length < STAGE_MAX) return [...stage, id];
  return [...stage.slice(0, STAGE_MAX - 1), id];
}

export function removeFromStage(stage: string[], id: string): string[] {
  return stage.filter((s) => s !== id);
}

/** Make a map the large one (first slot) without changing who is on stage. */
export function promoteOnStage(stage: string[], id: string): string[] {
  if (!stage.includes(id)) return stage;
  return [id, ...stage.filter((s) => s !== id)];
}

// ── The room: everybody on one canvas, each in their own area ────────────────

export interface RoomArea {
  id: string;
  x: number;
  y: number;
}

/** The space a map's shapes actually occupy (never smaller than a comfortable minimum). */
export function docBounds(doc: MapDoc): { x: number; y: number; w: number; h: number } {
  if (doc.shapes.length === 0) return { x: 0, y: 0, w: 600, h: 400 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const s of doc.shapes) {
    const { w, h } = boxOf(s);
    minX = Math.min(minX, s.x);
    minY = Math.min(minY, s.y);
    maxX = Math.max(maxX, s.x + w);
    maxY = Math.max(maxY, s.y + h);
  }
  return { x: minX, y: minY, w: Math.max(maxX - minX, 600), h: Math.max(maxY - minY, 400) };
}

/**
 * Lay everybody out in a near-square grid of equal cells with a gutter between
 * them. A cell is sized to the largest map in the room, not to the whole
 * drawing space, so the whole room stays readable on one screen.
 */
export function roomAreas(ids: string[], cellWidth: number, cellHeight: number, gutter = 160): RoomArea[] {
  const columns = Math.max(1, Math.ceil(Math.sqrt(ids.length)));
  return ids.map((id, i) => ({
    id,
    x: (i % columns) * (cellWidth + gutter),
    y: Math.floor(i / columns) * (cellHeight + gutter),
  }));
}

// ── Shared models: several valid views, at four altitudes ────────────────────

export type MapLevelNumber = 1 | 2 | 3 | 4;

export interface MapLevel {
  level: MapLevelNumber;
  name: string;
  /** The question a model at this level answers. */
  question: string;
}

/**
 * The four altitudes a shared model can sit at, highest abstraction first.
 * A deeper model unpacks ONE shape of the model above it, so every level
 * traces back up to the picture it came from.
 */
export const MAP_LEVELS: MapLevel[] = [
  { level: 1, name: 'Vision map', question: 'What is this, in one picture?' },
  { level: 2, name: 'Architecture map', question: 'What are its parts?' },
  { level: 3, name: 'Process flow', question: 'How does it work, step by step?' },
  { level: 4, name: 'Implementation blueprint', question: 'Exactly how is each step done?' },
];

export function mapLevel(level: number): MapLevel {
  return MAP_LEVELS.find((l) => l.level === level) ?? MAP_LEVELS[0];
}

export interface SharedModel {
  id: string;
  title: string;
  level: MapLevelNumber;
  /** Set when this model is the deeper view of one shape in another model. */
  parentModelId: string | null;
  parentShapeId: string | null;
  doc: MapDoc;
  /** Who is editing it now; one person at a time. */
  penHolderId: string | null;
}

/** The path from the top-level model down to this one. */
export function modelAncestry(models: SharedModel[], id: string): SharedModel[] {
  const byId = new Map(models.map((m) => [m.id, m]));
  const path: SharedModel[] = [];
  const seen = new Set<string>();
  let current = byId.get(id);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parentModelId ? byId.get(current.parentModelId) : undefined;
  }
  return path;
}

/**
 * The other valid views of the same thing: models that unpack the same shape
 * of the same parent (or, at the top, all top-level models). Includes itself.
 */
export function siblingModels(models: SharedModel[], model: SharedModel): SharedModel[] {
  return models.filter((m) => m.parentModelId === model.parentModelId && m.parentShapeId === model.parentShapeId);
}

/** The models that go deeper on one shape of a model. */
export function deeperModels(models: SharedModel[], modelId: string, shapeId: string): SharedModel[] {
  return models.filter((m) => m.parentModelId === modelId && m.parentShapeId === shapeId);
}

/**
 * Bring one map's shapes and lines into another, as new shapes. Used to carry
 * part of someone's own map into the shared model without disturbing what is
 * already there.
 */
export function copyInto(target: MapDoc, source: MapDoc): MapDoc {
  const idFor = new Map<string, string>();
  const nudge = target.shapes.length > 0 ? 40 : 0;
  const shapes = source.shapes.map((s) => {
    const id = newMapId('s');
    idFor.set(s.id, id);
    return clampToArea({ ...s, id, x: s.x + nudge, y: s.y + nudge });
  });
  const links = source.links
    .filter((l) => idFor.has(l.from) && idFor.has(l.to))
    .map((l) => ({ ...l, id: newMapId('l'), from: idFor.get(l.from)!, to: idFor.get(l.to)! }));
  return { shapes: [...target.shapes, ...shapes], links: [...target.links, ...links] };
}

/** Just these shapes of a map, and the lines that run between them. */
export function subsetDoc(doc: MapDoc, shapeIds: string[]): MapDoc {
  const keep = new Set(shapeIds);
  return {
    shapes: doc.shapes.filter((s) => keep.has(s.id)),
    links: doc.links.filter((l) => keep.has(l.from) && keep.has(l.to)),
  };
}

/** Read a stored shared model, discarding anything malformed. */
export function parseSharedModel(value: unknown): SharedModel | null {
  if (!value || typeof value !== 'object') return null;
  const r = value as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.title !== 'string') return null;
  const level = r.level === 2 || r.level === 3 || r.level === 4 ? r.level : 1;
  return {
    id: r.id,
    title: r.title,
    level,
    parentModelId: typeof r.parent_model_id === 'string' ? r.parent_model_id : null,
    parentShapeId: typeof r.parent_shape_id === 'string' ? r.parent_shape_id : null,
    doc: parseMapDoc(r.doc),
    penHolderId: typeof r.pen_entry_id === 'string' ? r.pen_entry_id : null,
  };
}

// ── Geometry shared by the canvas and the saved picture ──────────────────────

export interface Point {
  x: number;
  y: number;
}

type Placed = Pick<MapShape, 'kind' | 'size' | 'x' | 'y' | 'w' | 'h'>;

export function shapeCentre(shape: Placed): Point {
  const { w, h } = boxOf(shape);
  return { x: shape.x + w / 2, y: shape.y + h / 2 };
}

/**
 * Where a line from this shape's centre toward `toward` leaves the shape, so
 * lines meet the outline instead of a fixed dot on one side.
 */
export function shapeEdge(shape: Placed, toward: Point): Point {
  const { w, h } = boxOf(shape);
  const centre = shapeCentre(shape);
  const dx = toward.x - centre.x;
  const dy = toward.y - centre.y;
  const length = Math.hypot(dx, dy);
  if (length < 1) return centre;
  let reach: number;
  if (shape.kind === 'circle' || shape.kind === 'ring') {
    reach = 0.46 * w;
  } else if (shape.kind === 'point') {
    reach = 0.2 * w;
  } else {
    const halfW = 0.48 * w;
    const halfH = (shape.kind === 'rectangle' || shape.kind === 'rounded' ? 0.34 : 0.48) * h;
    reach = Math.min(halfW / Math.max(Math.abs(dx) / length, 1e-6), halfH / Math.max(Math.abs(dy) / length, 1e-6));
  }
  const t = Math.min(reach + 4, length / 2) / length;
  return { x: centre.x + dx * t, y: centre.y + dy * t };
}

// ── A map as a picture you can keep ──────────────────────────────────────────

/** Fixed colours for the saved picture: a file has no theme to follow. */
const PICTURE_TONE: Record<MapTone, string> = {
  neutral: '#3D4B6F',
  priority: '#D4A017',
  tension: '#C0533A',
  working: '#3A7D56',
};
const PICTURE_LINK: Record<MapLinkKind, string> = {
  leads_to: '#3D4B6F',
  supports: '#3A7D56',
  conflicts: '#C0533A',
  part_of: '#6B7A96',
};

function xmlText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Break a label into lines that fit a width, at word boundaries. */
export function wrapLabel(label: string, width: number, fontSize = 14): string[] {
  const perLine = Math.max(6, Math.floor(width / (fontSize * 0.55)));
  const lines: string[] = [];
  // A line break the person typed is kept; long lines wrap at word boundaries.
  for (const paragraph of label.trim().split('\n')) {
    let line = '';
    for (const word of paragraph.trim().split(/\s+/).filter(Boolean)) {
      if (line && (line + ' ' + word).length > perLine) {
        lines.push(line);
        line = word;
      } else {
        line = line ? `${line} ${word}` : word;
      }
    }
    if (line) lines.push(line);
  }
  return lines;
}

function pictureShape(kind: MapShapeKind, color: string): string {
  const fill = `fill="${color}" fill-opacity="0.12" stroke="${color}" stroke-width="2.5" vector-effect="non-scaling-stroke"`;
  switch (kind) {
    case 'circle':
      return `<circle cx="50" cy="50" r="46" ${fill}/>`;
    case 'rectangle':
      return `<rect x="4" y="16" width="92" height="68" ${fill}/>`;
    case 'rounded':
      return `<rect x="4" y="16" width="92" height="68" rx="20" ${fill}/>`;
    case 'triangle':
      return `<polygon points="50,6 96,92 4,92" ${fill}/>`;
    case 'diamond':
      return `<polygon points="50,4 96,50 50,96 4,50" ${fill}/>`;
    case 'hexagon':
      return `<polygon points="26,8 74,8 98,50 74,92 26,92 2,50" ${fill}/>`;
    case 'ring':
      return `<circle cx="50" cy="50" r="46" fill="none" stroke="${color}" stroke-width="4" vector-effect="non-scaling-stroke"/>`;
    case 'point':
      return `<circle cx="50" cy="50" r="16" fill="${color}"/>`;
    case 'layer':
      return `<rect x="1" y="3" width="98" height="94" ${fill}/>`;
    case 'phase':
      return `<polygon points="1,3 86,3 99,50 86,97 1,97 14,50" ${fill}/>`;
    case 'note':
      // Words only: nothing is drawn around them.
      return '';
  }
}

/**
 * Draw a map as a standalone SVG picture: the same shapes, colours and lines,
 * with its title above. Opens in any browser and drops into slides and docs.
 */
export function mapToSvg(doc: MapDoc, title: string): string {
  const pad = 40;
  const titleHeight = title.trim() ? 48 : 0;
  const boxes = doc.shapes.map((s) => ({ s, ...boxOf(s) }));
  const minX = boxes.length ? Math.min(...boxes.map((b) => b.s.x)) : 0;
  const minY = boxes.length ? Math.min(...boxes.map((b) => b.s.y)) : 0;
  const maxX = boxes.length ? Math.max(...boxes.map((b) => b.s.x + b.w)) : 400;
  const maxY = boxes.length ? Math.max(...boxes.map((b) => b.s.y + b.h + (b.s.kind === 'point' ? 24 : 0))) : 200;
  const width = Math.max(maxX - minX + pad * 2, 360);
  const height = maxY - minY + pad * 2 + titleHeight;
  const ox = pad - minX;
  const oy = pad - minY + titleHeight;
  const font = 'font-family="Helvetica, Arial, sans-serif"';
  const byId = new Map(doc.shapes.map((s) => [s.id, s]));

  const parts: string[] = [];
  parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`);
  parts.push('<defs>');
  for (const kind of Object.keys(PICTURE_LINK) as MapLinkKind[]) {
    parts.push(
      `<marker id="arrow-${kind}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="${PICTURE_LINK[kind]}"/></marker>`,
    );
  }
  parts.push('</defs>');
  parts.push(`<rect width="${width}" height="${height}" fill="#FFFFFF"/>`);
  if (titleHeight) {
    parts.push(`<text x="${pad}" y="${pad}" ${font} font-size="20" font-weight="600" fill="#1F2937">${xmlText(title.trim())}</text>`);
  }

  // Boundaries and layers first, so other shapes sit on them — as on the canvas.
  const ordered = [...boxes].sort((a, b) => Number(b.s.kind === 'ring' || b.s.kind === 'layer') - Number(a.s.kind === 'ring' || a.s.kind === 'layer'));
  for (const { s, w, h } of ordered) {
    const color = PICTURE_TONE[s.tone];
    parts.push(
      `<svg x="${s.x + ox}" y="${s.y + oy}" width="${w}" height="${h}" viewBox="0 0 100 100" preserveAspectRatio="none">${pictureShape(s.kind, color)}</svg>`,
    );
  }
  const linkLabels: string[] = [];
  for (const link of doc.links) {
    const from = byId.get(link.from);
    const to = byId.get(link.to);
    if (!from || !to) continue;
    const a = shapeEdge(from, shapeCentre(to));
    const b = shapeEdge(to, shapeCentre(from));
    const style = linkStyle(link.kind);
    parts.push(
      `<line x1="${a.x + ox}" y1="${a.y + oy}" x2="${b.x + ox}" y2="${b.y + oy}" stroke="${PICTURE_LINK[link.kind]}" stroke-width="2"${style.dashed ? ' stroke-dasharray="7 6"' : ''}${style.arrow ? ` marker-end="url(#arrow-${link.kind})"` : ''}/>`,
    );
    const words = link.label?.trim();
    if (words) {
      // What the line says, on a white patch at its middle so it stays readable.
      const mx = (a.x + b.x) / 2 + ox;
      const my = (a.y + b.y) / 2 + oy;
      const patch = words.length * 6.6 + 12;
      linkLabels.push(
        `<rect x="${mx - patch / 2}" y="${my - 10}" width="${patch}" height="20" rx="4" fill="#FFFFFF"/><text x="${mx}" y="${my + 4}" text-anchor="middle" ${font} font-size="12" fill="${PICTURE_LINK[link.kind]}">${xmlText(words)}</text>`,
      );
    }
  }
  for (const { s, w, h } of boxes) {
    const lines = wrapLabel(s.label, s.kind === 'point' ? 160 : w * 0.82);
    if (lines.length === 0) continue;
    const cx = s.x + ox + w / 2;
    const lineHeight = 17;
    let top: number;
    if (s.kind === 'point') top = s.y + oy + h * 0.72 + 14;
    else if (s.kind === 'ring') top = s.y + oy + h * 0.14 + 14;
    else if (s.kind === 'triangle') top = s.y + oy + h * 0.84 - (lines.length - 1) * lineHeight;
    else top = s.y + oy + h / 2 - ((lines.length - 1) * lineHeight) / 2 + 5;
    parts.push(
      `<text x="${cx}" y="${top}" text-anchor="middle" ${font} font-size="14" fill="#1F2937">${lines
        .map((line, i) => `<tspan x="${cx}" dy="${i === 0 ? 0 : lineHeight}">${xmlText(line)}</tspan>`)
        .join('')}</text>`,
    );
  }
  parts.push(...linkLabels);
  parts.push('</svg>');
  return parts.join('');
}
